import {
  BellSlash,
  BellSimple,
  ChatCircle,
  Flag,
  PaperPlaneTilt,
  Plus,
  Trash,
  UserPlus,
} from '@phosphor-icons/react';
import { UNREAD_CAP, type Conversation, type PublicUser } from '@sirocco/shared';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { messages, users as usersApi, type MessageEcrit } from '../../api/endpoints';
import { ScreenError, ScreenLoader, ServerScreen } from '../../components/layout/Screens';
import { Avatar, GroupAvatar } from '../../components/ui/Avatar';
import { IconButton } from '../../components/ui/Button';
import { BadgeEmoji, EmptyState } from '../../components/ui/Bits';
import { SearchField } from '../../components/ui/Field';
import { ConfirmDialog, useConfirm } from '../../components/ui/Confirm';
import { RowMenu, RowMenuItem, type RowAnchor } from '../../components/ui/RowMenu';
import { readScreenCache, seedScreenCache, useAsync, useDebounced } from '../../hooks/useAsync';
import { useLongPress } from '../../hooks/useLongPress';
import { useSession } from '../../store/SessionContext';
import { useFlashOnArrival } from '../../utils/flash';
import { compte, conversationTime } from '../../utils/format';
import { aplatir, extrait, trouverDans } from '../../utils/recherche';
import { reportPath } from '../report/ReportScreen';
import { HandOverSheet, needsSuccessor } from './HandOverSheet';
import { t } from '../../i18n';

/**
 * L'enveloppe qui s'ouvre sur une demande.
 *
 * Aucun signe de Phosphor ne dit « message qu'on ne m'a pas encore
 * adresse » : la bulle disait « discussion », c'est-a-dire ce que fait
 * deja tout l'onglet. Une enveloppe et un plus le disent, du meme trait
 * et de la meme graisse que ses voisines pour que la barre reste d'une
 * seule main.
 *
 * Le coin haut-droit de l'enveloppe est ouvert plutot que barre par le
 * plus : deux traits qui se croisent font une tache a vingt pixels.
 */
function EnveloppePlus({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 256 256"
      fill="none"
      stroke="currentColor"
      strokeWidth={16}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M164 64H44a16 16 0 0 0-16 16v96a16 16 0 0 0 16 16h138a16 16 0 0 0 16-16v-56" />
      <path d="M31 74l82 60 57-41" />
      <path d="M182 82h52M208 56v52" />
    </svg>
  );
}

/**
 * Onglet Messages : les conversations du spot courant.
 *
 * Les lignes ne sont pas separees par un filet mais par du vide : c'est
 * ce qui donne a la liste son calme sur les maquettes.
 */
/* Ce que la recherche des discussions garde d'un passage a l'autre. */
const RECHERCHE = 'messages:recherche';
const TEXTES = 'messages:textes';

export function MessagesScreen() {
  const { user } = useSession();
  const navigate = useNavigate();
  const { data, loading, error, reload, setData } = useAsync(
    () => messages.conversations(),
    [],
    'conversations',
  );
  // Les demandes d'ami se chargent a part : elles n'ont rien a voir avec
  // les conversations, et leur absence ne doit pas retarder la liste.
  /*
    Deux compteurs de ce qui n'a pas encore ete vu. Ils ne comptent pas
    ce qui reste a traiter : ouvrir l'ecran eteint le point, repondre ou
    non ne le concerne pas.
  */
  const { data: nouvellesAmities } = useAsync(
    () => usersApi.newRequestCount(),
    [],
    'friend-request-count',
  );
  const requestCount = nouvellesAmities ?? 0;
  /* Les demandes de message, comptees a part : elles ne concernent ni les
     conversations ouvertes ni les demandes d'ami. */
  const { data: messageRequests } = useAsync(
    () => messages.requestCount(),
    [],
    'message-request-count',
  );
  const pendingMessages = messageRequests ?? 0;

  /*
    Chercher une discussion.

    Par son nom, et par le nom de ceux qui y sont : chercher « Naoufel »
    doit trouver votre fil a deux, mais aussi le groupe de la sortie ou il
    est. Sans accent ni majuscule, comme partout ailleurs — « chaima »
    trouve « Chaïma.H ».

    Rien n'est relu : la liste est deja la, on la filtre sous les yeux.

    La recherche survit a l'ecran. Ouvrir une discussion demonte la
    liste, et revenir en arriere la rendait vide : il fallait retaper le
    mot pour retrouver les autres resultats. Elle est donc gardee dans le
    cache de session — celui qui se vide quand le membre ou le spot
    change — et la position de defilement, retenue par adresse, suit.
  */
  const [recherche, poserRecherche] = useState(() => readScreenCache<string>(RECHERCHE) ?? '');
  const setRecherche = (terme: string) => {
    seedScreenCache(RECHERCHE, terme);
    poserRecherche(terme);
  };
  const cherche = aplatir(recherche.trim());
  const vues = (data ?? []).filter(
    (conversation) =>
      !cherche ||
      aplatir(conversation.title).includes(cherche) ||
      conversation.members.some((membre) => aplatir(membre.username).includes(cherche)),
  );

  /*
    Et dans ce qui s'y est dit.

    Comme WhatsApp : chercher « oui » propose aussi chaque discussion ou
    le mot a ete ecrit, avec le message. Les fils ne se lisent qu'une fois
    qu'on cherche, et se relisent quand la liste change — un message
    arrive pendant qu'on cherche doit pouvoir etre trouve.

    Ce qui a ete lu reste dans le cache de session, comme les fils
    eux-memes : au retour d'une discussion, les resultats sont la des la
    premiere image, et la position de defilement peut s'y reposer.
  */
  const [ecrits, setEcrits] = useState<MessageEcrit[]>(
    () => readScreenCache<MessageEcrit[]>(TEXTES) ?? [],
  );
  const enRecherche = cherche !== '';
  const lesFils = (data ?? []).map((c) => `${c.id}:${c.lastMessage?.id ?? ''}`).join(',');
  useEffect(() => {
    if (!enRecherche || !data?.length) return;
    let vivant = true;
    void messages
      .textes(data.map((conversation) => conversation.id))
      .then((lus) => {
        seedScreenCache(TEXTES, lus);
        if (vivant) setEcrits(lus);
      })
      .catch(() => undefined);
    return () => {
      vivant = false;
    };
    // `lesFils` resume `data` : relire a chaque rendu serait relire pour rien.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enRecherche, lesFils]);

  /*
    Et parmi les comptes.

    Retirer une conversation de sa liste la faisait sortir de la
    recherche : on tapait le nom de quelqu'un a qui l'on avait ecrit, et
    rien ne venait — plus moyen de lui ecrire sans passer par son profil.
    La recherche propose donc aussi les membres dont le pseudo
    correspond, sauf ceux dont le tete-a-tete est deja dans la liste : il
    se trouve deja au-dessus. Ecrire a l'un d'eux rouvre le fil d'avant,
    s'il y en avait un (`openDirect`), au lieu d'en creer un second.
  */
  const chercheDiffere = useDebounced(cherche);
  /* Les comptes, et le mot pour lequel ils ont ete lus : « aucun
     resultat » ne se dit qu'une fois la reponse arrivee pour ce mot-la. */
  const [comptes, setComptes] = useState<{ pour: string; liste: PublicUser[] }>({
    pour: '',
    liste: [],
  });
  useEffect(() => {
    if (!chercheDiffere) return setComptes({ pour: '', liste: [] });
    let vivant = true;
    void usersApi
      .search(chercheDiffere)
      .catch(() => [] as PublicUser[])
      .then((liste) => {
        if (vivant) setComptes({ pour: chercheDiffere, liste });
      });
    return () => {
      vivant = false;
    };
  }, [chercheDiffere]);
  const enTeteATete = new Set(
    (data ?? [])
      .filter((conversation) => conversation.type === 'direct')
      .flatMap((conversation) => conversation.members.map((membre) => membre.id)),
  );
  const autres = enRecherche ? comptes.liste.filter((membre) => !enTeteATete.has(membre.id)) : [];

  const [ouvrant, setOuvrant] = useState<string | null>(null);
  async function ecrireA(membre: PublicUser) {
    if (ouvrant) return;
    setOuvrant(membre.id);
    try {
      const conversation = await messages.openDirect(membre.id);
      navigate(`/app/messages/${conversation.id}`);
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : t.sorties.fiche.ecrireImpossible);
    } finally {
      setOuvrant(null);
    }
  }

  const parId = new Map((data ?? []).map((conversation) => [conversation.id, conversation]));
  /* Du plus recent au plus ancien, comme le reste de la liste. */
  const trouves = enRecherche
    ? ecrits
        .flatMap((ecrit) => {
          const conversation = parId.get(ecrit.conversationId);
          const ou = trouverDans(ecrit.content, cherche);
          return conversation && ou ? [{ ecrit, conversation, ou }] : [];
        })
        .sort((a, b) => b.ecrit.createdAt.localeCompare(a.ecrit.createdAt))
    : [];

  /*
    La liste se tient a jour toute seule : un message qui arrive dans un
    fil le fait remonter, une conversation qui nait apparait. Sans cela,
    il fallait changer d'onglet et revenir.
  */
  useEffect(() => {
    return messages.watchConversations(setData);
  }, [setData]);

  /*
    Qui ecrit, sans ouvrir le fil.

    Une ecoute par conversation, mais sur la plus petite branche de la
    base : un horodatage par personne, et rien du message. La cle est
    l'ensemble des identifiants — se rebrancher a chaque relecture de la
    liste referait toutes les ecoutes pour rien.
  */
  const [frappent, setFrappent] = useState<Set<string>>(new Set());
  const cleDesFils = (data ?? []).map((row) => row.id).join(',');

  useEffect(() => {
    if (!cleDesFils) return setFrappent(new Set());
    return messages.watchFrappeDesFils(cleDesFils.split(','), setFrappent);
  }, [cleDesFils]);

  const [notice, setNotice] = useState<string | null>(null);
  /*
    Le retour d'un ecran de signalement. Son accuse de reception est
    ramasse puis jete : rien ne s'affiche ici. Il faut quand meme le
    ramasser, sinon il resterait en attente et s'afficherait sur le
    premier ecran ou l'on passerait ensuite.
  */
  useFlashOnArrival(() => {});

  /*
    La conversation qu'on tient, et l'endroit ou elle se trouve. L'etat
    vit dans l'ecran et non dans la ligne : pose dans la zone defilante,
    le menu s'ouvrirait sous le pli des que la ligne est en bas.
  */
  const [held, setHeld] = useState<{ conversation: Conversation; anchor: RowAnchor } | null>(null);
  /* Le groupe qu'on quitte, tant qu'on n'a pas dit a qui on le laisse. */
  const [handOver, setHandOver] = useState<Conversation | null>(null);
  /*
    Quitter un groupe ne se defait pas — il faut s'y faire reinviter — et
    un fil supprime emporte ce qu'on y avait ecrit. La question se pose
    donc au centre de l'ecran, comme pour un post qu'on efface.
  */
  const { pending, request, cancel, confirm } = useConfirm();

  /** Referme le menu, puis pose la question. Jamais les deux ensemble. */
  function ask(question: string, run: () => void) {
    setHeld(null);
    request(question, run);
  }

  /**
   * Ce qui n'a pas marche, et rien d'autre.
   *
   * Une action qui aboutit se voit : la conversation disparait, la
   * cloche barree s'allume. L'annoncer en plus, c'etait un bandeau qui
   * surgissait en haut de la liste pour dire ce qu'on venait de faire.
   * Un echec, lui, ne se voit pas — d'ou ce message, qui ne sert plus
   * qu'a ca.
   */
  function warn(message: string) {
    setNotice(message);
    setTimeout(() => setNotice(null), 2600);
  }

  /**
   * Coupe ou rend le son d'une conversation.
   *
   * Ce qui est coupe, ce sont les notifications du telephone. Elles
   * n'existent pas encore — il y faut un compte Apple payant — mais le
   * reglage se pose des maintenant : il sera lu le jour ou elles
   * arriveront, sans que personne ait a repasser sur ses fils.
   */
  async function mute(conversation: Conversation) {
    setHeld(null);
    try {
      const updated = await messages.mute(conversation.id);
      setData((previous) => previous.map((row) => (row.id === updated.id ? updated : row)));
    } catch {
      reload();
      warn(t.carte.ecran.impossible);
    }
  }

  /**
   * Retire une conversation de ma liste.
   * Un groupe se quitte au passage : y rester sans le voir n'aurait pas
   * de sens. Le fil d'une directe, lui, reste chez l'autre.
   */
  async function remove(conversation: Conversation, successorId?: string) {
    setNotice(null);
    setData((previous) => previous.filter((row) => row.id !== conversation.id));
    try {
      await messages.removeConversation(conversation.id, successorId);
    } catch (cause) {
      reload();
      warn(cause instanceof Error ? cause.message : t.fil.commun.suppressionImpossible);
    }
  }

  /**
   * Partir d'ici, ou passer d'abord la main.
   *
   * L'administrateur qui s'en va designe son repreneur — sans quoi le
   * groupe se retrouverait sans personne pour le tenir. La question se
   * pose donc ici aussi, et pas seulement dans la fiche du groupe :
   * renvoyer l'utilisateur la-bas etait une impasse deguisee en message.
   */
  function leaveOrHandOver(conversation: Conversation) {
    if (needsSuccessor(conversation, user?.id)) {
      setHeld(null);
      setHandOver(conversation);
      return;
    }
    ask(
      conversation.type === 'group'
        ? t.messages.liste.quitterQ(conversation.title)
        : t.messages.liste.supprimerQ,
      () => void remove(conversation),
    );
  }

  return (
    <ServerScreen
      /* « Nouveau groupe » et les deux boites restent en place : on tire
         la liste, pas la barre qui la commande. */
      sticky={
        <>
          <div className="ms-head">
            <Link to="/app/messages/new-group" className="ms-head__new">
              <Plus size={16} weight="bold" />
              {t.messages.liste.nouveauGroupe}
            </Link>

            {/*
          Les demandes de message, a gauche des demandes d'ami : deux
          boites d'entree voisines, de meme taille, et qui ne se
          confondent pas — l'une amene des messages, l'autre des gens.
        */}
            <IconButton
              label={
                pendingMessages > 0
                  ? t.messages.liste.demandesMessageEnAttente(pendingMessages)
                  : t.messages.liste.demandesMessage
              }
              variant="ghost"
              size={34}
              className="ms-head__friends"
              onClick={() => navigate('/app/messages/requests')}
            >
              <EnveloppePlus size={21} />
              {pendingMessages > 0 && (
                <span className="pastille pastille--compte">{compte(pendingMessages)}</span>
              )}
            </IconButton>

            <IconButton
              label={
                requestCount > 0
                  ? t.messages.liste.demandesAmiEnAttente(requestCount)
                  : t.messages.liste.ajouterAmis
              }
              variant="ghost"
              size={34}
              className="ms-head__friends"
              onClick={() => navigate('/app/messages/friends')}
            >
              <UserPlus size={20} />
              {/* La pastille porte son nombre : combien de gens attendent
                  une reponse derriere cette icone. Le libelle le repete
                  pour les lecteurs d'ecran, qui ne lisent pas un rond. */}
              {requestCount > 0 && (
                <span className="pastille pastille--compte">{compte(requestCount)}</span>
              )}
            </IconButton>
          </div>

        </>
      }
      overlay={
        <>
          <RowMenu anchor={held?.anchor ?? null} onClose={() => setHeld(null)}>
            {held && (
              <>
                <RowMenuItem
                  icon={
                    held.conversation.muted ? <BellSimple size={20} /> : <BellSlash size={20} />
                  }
                  onClick={() => void mute(held.conversation)}
                >
                  {held.conversation.muted ? t.messages.liste.reactiverSon : t.messages.liste.sourdine}
                </RowMenuItem>

                {/*
                  Sauf en face du compte de l'application : ce fil est la
                  porte vers l'equipe, et le signalement y menerait a
                  elle-meme. Un groupe ou elle figure garde le sien.
                */}
                {!(
                  held.conversation.type === 'direct' &&
                  held.conversation.members.some((membre) => membre.officiel)
                ) && (
                  <RowMenuItem
                    icon={<Flag size={20} />}
                    onClick={() => navigate(reportPath('conversation', held.conversation.id))}
                  >
                    {t.messages.liste.signaler}
                  </RowMenuItem>
                )}

                <RowMenuItem
                  icon={<Trash size={20} />}
                  tone="danger"
                  onClick={() => leaveOrHandOver(held.conversation)}
                >
                  {held.conversation.type === 'group'
                    ? t.messages.commun.quitterGroupe
                    : t.messages.liste.supprimer}
                </RowMenuItem>
              </>
            )}
          </RowMenu>

          <HandOverSheet
            open={handOver !== null}
            members={handOver?.members ?? []}
            meId={user?.id}
            orphan={!handOver?.adminId}
            onClose={() => setHandOver(null)}
            onPick={(member) => {
              const target = handOver;
              setHandOver(null);
              if (!target) return;
              request(
                t.messages.liste.laisserEtPartirQ(target.title, member.username),
                () => void remove(target, member.id),
              );
            }}
          />

          <ConfirmDialog pending={pending} onCancel={cancel} onConfirm={confirm} />
        </>
      }
    >
      {loading && <ScreenLoader />}
      {error && <ScreenError message={error} onRetry={reload} />}

      {notice && <p className="ms-notice">{notice}</p>}

      {/*
        La meme barre que sur les evenements et dans « Ajouter des amis »,
        a la tete de la liste qu'elle filtre : elle descend avec elle quand
        on tire. Meme sur une liste vide : on y cherche aussi les comptes,
        et c'est justement apres avoir tout retire qu'on en a besoin.
      */}
      {data && (
        <SearchField
          tone="spot"
          className="ms-search ms-search--liste search--croix-du-fil"
          placeholder={t.messages.liste.rechercher}
          value={recherche}
          onChange={(event) => setRecherche(event.target.value)}
          onClear={() => setRecherche('')}
        />
      )}

      {/*
        Une liste vide n'est pas une page vide. Sans un mot, l'ecran
        laissait croire a un chargement qui n'aboutit pas — alors qu'il
        n'y a simplement encore rien, ce qui est l'etat normal d'un
        compte qui vient d'arriver. Sous la barre de recherche, et non
        au-dessus : la barre est l'outil de l'ecran, le mot dit ce qu'il y
        a dessous.
      */}
      {data && data.length === 0 && !enRecherche && (
        <EmptyState
          icon={<ChatCircle size={38} />}
          title={t.messages.liste.vide}
          hint={t.messages.liste.videAide}
        />
      )}

      {data &&
        enRecherche &&
        vues.length === 0 &&
        trouves.length === 0 &&
        autres.length === 0 &&
        comptes.pour === cherche && (
          <p className="ms-notice ms-notice--recherche">
            {t.messages.liste.aucuneDiscussion(recherche.trim())}
          </p>
        )}

      {/* Deux familles de resultats : elles se nomment, sinon rien ne dit
          pourquoi une discussion au nom sans rapport est la. */}
      {enRecherche && vues.length > 0 && (trouves.length > 0 || autres.length > 0) && (
        <h2 className="ms-section ms-section--resultats">{t.messages.liste.discussions}</h2>
      )}

      {vues.length > 0 && (
        <div className="ms-list">
          {vues.map((conversation) => (
            <ConversationRow
              key={conversation.id}
              conversation={conversation}
              myId={user?.id ?? ''}
              ecrit={frappent.has(conversation.id)}
              onHold={(anchor) => setHeld({ conversation, anchor })}
            />
          ))}
        </div>
      )}

      {autres.length > 0 && (
        <>
          <h2 className="ms-section ms-section--resultats">{t.messages.liste.comptes}</h2>
          <div className="ms-list">
            {autres.map((membre) => (
              <CompteTrouve
                key={membre.id}
                membre={membre}
                ouvre={ouvrant === membre.id}
                onEcrire={() => void ecrireA(membre)}
              />
            ))}
          </div>
        </>
      )}

      {trouves.length > 0 && (
        <>
          <h2 className="ms-section ms-section--resultats">{t.messages.liste.messages}</h2>
          <div className="ms-list">
            {trouves.map(({ ecrit, conversation, ou }) => (
              <MessageTrouve
                key={`${ecrit.conversationId}/${ecrit.id}`}
                ecrit={ecrit}
                conversation={conversation}
                ou={ou}
                myId={user?.id ?? ''}
              />
            ))}
          </div>
        </>
      )}
    </ServerScreen>
  );
}

/**
 * Un compte trouve par son pseudo : son visage, son nom, et l'avion.
 *
 * Toute la ligne ecrit : c'est ce qu'on vient chercher ici. Le profil
 * reste a une touche de plus, depuis la conversation.
 */
function CompteTrouve({
  membre,
  ouvre,
  onEcrire,
}: {
  membre: PublicUser;
  ouvre: boolean;
  onEcrire: () => void;
}) {
  return (
    <button type="button" className="ms-row ms-row--compte" onClick={onEcrire} disabled={ouvre}>
      <Avatar user={membre} size={44} showOnline />
      <span className="ms-row__body">
        <span className="ms-row__title">
          {membre.username}
          <BadgeEmoji user={membre} />
        </span>
        <span className="ms-row__preview">{t.messages.liste.envoyerUnMessage}</span>
      </span>
      <span className="ms-row__side">
        <PaperPlaneTilt className="sens-x" size={20} />
      </span>
    </button>
  );
}

/**
 * Un message trouve : la discussion, le passage, le mot en gras.
 *
 * Le toucher ouvre la discussion sur ce message-la, qui s'allume un
 * instant — on arrive ou le mot a ete dit, pas au bas du fil.
 */
function MessageTrouve({
  ecrit,
  conversation,
  ou,
  myId,
}: {
  ecrit: MessageEcrit;
  conversation: Conversation;
  ou: [number, number];
  myId: string;
}) {
  const isGroup = conversation.type === 'group';
  const other = conversation.members.find((member) => member.id !== myId);
  const { avant, trouve, apres } = extrait(ecrit.content, ou);
  /* Qui l'a dit : moi, ou, dans un groupe, celui qui l'a ecrit. A deux,
     l'autre est deja le titre de la ligne. */
  const auteur =
    ecrit.senderId === myId
      ? t.messages.liste.toiDeuxPoints
      : isGroup
        ? t.messages.liste.auteur(
            conversation.members.find((member) => member.id === ecrit.senderId)?.username ??
              t.commun.quelquun,
          )
        : '';

  return (
    <Link
      to={`/app/messages/${conversation.id}`}
      state={{ message: ecrit.id }}
      className="ms-row"
    >
      {isGroup ? (
        <GroupAvatar size={44} rounded photoUrl={conversation.avatarUrl} />
      ) : (
        <Avatar user={other} size={44} showOnline />
      )}
      <span className="ms-row__body">
        <span className="ms-row__title">
          {conversation.title}
          {!isGroup && <BadgeEmoji user={other} />}
        </span>
        <span className="ms-row__preview">
          {auteur}
          {avant}
          <mark className="ms-trouve">{trouve}</mark>
          {apres}
        </span>
      </span>
      <span className="ms-row__side">
        <span className="ms-row__time">{conversationTime(ecrit.createdAt)}</span>
      </span>
    </Link>
  );
}

/**
 * Une ligne : avatar, apercu du fil, heure et pastille de non-lus.
 *
 * Un appui long ouvre ses actions. Le balayage servait a cela, mais il
 * sert maintenant a changer d'onglet : deux gestes horizontaux dans le
 * meme ecran, l'un ne pouvait que voler l'autre.
 *
 * La ligne rend sa position au passage : le menu se pose contre elle, et
 * seule la ligne sait ou elle est.
 */
export function ConversationRow({
  conversation,
  myId,
  ecrit = false,
  onHold,
}: {
  conversation: Conversation;
  myId: string;
  /** Quelqu'un d'autre y ecrit en ce moment. */
  ecrit?: boolean;
  /** Absent : la ligne n'ouvre aucun menu — c'est le cas des demandes. */
  onHold?: (anchor: RowAnchor) => void;
}) {
  const row = useRef<HTMLAnchorElement>(null);

  const hold = useLongPress(() => {
    const node = row.current;
    const screen = node?.closest('.server-screen');
    if (!node || !screen || !onHold) return;

    // Coordonnees relatives a l'ecran, ou le menu est pose.
    const line = node.getBoundingClientRect();
    const frame = screen.getBoundingClientRect();
    onHold({
      top: line.top - frame.top,
      bottom: line.bottom - frame.top,
      left: line.left - frame.left,
      width: line.width,
    });
  });
  const { lastMessage } = conversation;
  const nonLus = conversation.unreadCount;
  const isGroup = conversation.type === 'group';
  // Dans un tete-a-tete, l'avatar est celui de l'autre, pas le mien.
  const other = conversation.members.find((member) => member.id !== myId);

  return (
    <Link
      ref={row}
      to={`/app/messages/${conversation.id}`}
      className="ms-row"
      {...(onHold ? hold : {})}
    >
      {isGroup ? (
        <GroupAvatar size={44} rounded photoUrl={conversation.avatarUrl} />
      ) : (
        <Avatar user={other} size={44} showOnline />
      )}

      <span className="ms-row__body">
        <span className="ms-row__title">
          {conversation.title}
          {!isGroup && <BadgeEmoji user={other} />}
        </span>
        {/*
          « écrit... » prend la place de l'apercu tant que ca dure : les
          deux disent ce qui se passe dans ce fil, et le second est plus
          frais. Dans un groupe, on ne nomme personne — la ligne n'a pas
          la place, et le nom n'ajouterait rien a l'invitation d'ouvrir.
        */}
        {ecrit ? (
          <span className="ms-row__preview ms-row__preview--ecrit">{t.messages.commun.ecrit}</span>
        ) : (
          previewText(conversation) !== null && (
            <span className={`ms-row__preview ${previewTone(conversation)}`.trim()}>
              {previewText(conversation)}
            </span>
          )
        )}
      </span>

      {/* Une conversation encore vide ne porte rien a droite, sauf la
          sourdine : son apercu dit deja qu'il n'y a pas de message. */}
      {(lastMessage || conversation.muted || nonLus > 0) && (
        <span className="ms-row__side">
          {lastMessage && (
            <span className="ms-row__time">{conversationTime(lastMessage.createdAt)}</span>
          )}
          {(conversation.muted || nonLus > 0) && (
            <span className="ms-row__marques">
              {/* Une sourdine qui ne se voit pas est une sourdine qu'on oublie. */}
              {conversation.muted && (
                <BellSlash
                  className="ms-row__muted"
                  size={15}
                  aria-label={t.messages.liste.enSourdine}
                />
              )}
              {/*
                Combien de messages attendent, dans un rond a la couleur du
                spot, sous l'heure : ce que toutes les messageries font, et
                ce qu'on cherche d'un coup d'oeil en ouvrant l'onglet. Gris
                en sourdine — on a demande a ne pas etre presse.
              */}
              {nonLus > 0 && (
                <span
                  className={`ms-row__compte${conversation.muted ? ' ms-row__compte--sourdine' : ''}`}
                >
                  {unreadLabel(nonLus)}
                </span>
              )}
            </span>
          )}
        </span>
      )}
    </Link>
  );
}

/**
 * Le nombre de non-lus, ou son plafond suivi d'un plus.
 * La liste ne lit que la fin de chaque fil : au-dela elle ne sait plus
 * compter, et l'annoncer vaut mieux qu'un chiffre arrete en silence.
 */
function unreadLabel(count: number): string {
  return count >= UNREAD_CAP ? `${UNREAD_CAP}+` : String(count);
}

/** Texte affiche sous le titre : non-lus, dernier message, ou invitation. */
function previewText({ lastMessage, unreadCount, dejaEcrit }: Conversation): string | null {
  /* L'apercu annonce, le rond sous l'heure compte : le chiffre ne se dit
     qu'une fois, dans un groupe comme a deux. */
  if (unreadCount > 0) {
    return unreadCount === 1 ? t.messages.commun.nouveauMessage : t.messages.liste.nonLus;
  }
  // Un dernier message retire n'a plus de contenu a montrer : sans ce
  // cas, l'apercu tombait sur une ligne vide.
  if (lastMessage?.deleted) return t.messages.commun.supprime;
  /*
    Un vocal ou une photo n'ont pas de texte : sans ces deux lignes,
    l'apercu tombait sur du vide et la conversation avait l'air muette.
  */
  if (lastMessage?.systeme === 'amitie-rompue') return t.messages.liste.plusAmis;
  /* Le pseudo de celui qui part voyage dans le contenu : il n'est plus
     dans les membres du groupe, on ne pourrait pas l'y retrouver. */
  if (lastMessage?.systeme === 'sortie-groupe') {
    return t.messages.liste.systeme.quitte(lastMessage.content || t.commun.quelquun);
  }
  if (lastMessage?.systeme === 'retire-groupe') {
    return t.messages.liste.systeme.retire(lastMessage.content || t.commun.quelquun);
  }
  if (lastMessage?.systeme === 'ajout-groupe') {
    return t.messages.liste.systeme.rejoint(lastMessage.content || t.commun.quelquun);
  }
  if (lastMessage?.call) return lastMessage.call.missed ? t.messages.liste.appelManque : t.messages.liste.appel;
  if (lastMessage?.voice) return t.messages.commun.vocal;
  if (lastMessage && !lastMessage.content && lastMessage.imageUrl) return t.messages.commun.photo;
  if (lastMessage) return lastMessage.content;
  /* On s'est deja ecrit, et les messages se sont effaces : rien a inviter. */
  if (dejaEcrit) return null;
  return t.messages.liste.premier;
}

function previewTone({ lastMessage, unreadCount, supprimeNonLu }: Conversation): string {
  if (unreadCount > 0 || supprimeNonLu) return 'ms-row__preview--unread';
  return lastMessage ? '' : 'ms-row__preview--empty';
}
